import React from 'react';
import dayjs from 'dayjs';
import {Button, Card, DatePicker, Form, Input, Spin} from 'antd';
import {dateFormat} from '../../../../lib/time';
import {useGetPersonQuery, useUpdatePersonMutation} from '../../../../store/api/accountApi';
import {selectIsStaff} from '../../../../store/slices/userSlice';
import {useAppSelector} from '../../../../hooks/useStore';
import {messageType, showMessages} from '../../../../helpers/message';
import {Person} from '../../../../types/accountTypes';
import {BaseError} from '../../../../types/apiTypes';
import {nothingToUpdate} from '../../../../helpers/text';

import {formInCardLayout, formItemFullWidth} from '../../../../assets/styles';

const PersonalInfoCard = () => {
    const personID = useAppSelector(state => state.user.person)?.id;
    const isStaff = useAppSelector(selectIsStaff);
    const [form] = Form.useForm();

    const [updatePerson, {error: updateError, isError: isUpdateError, isSuccess: updateSuccess, isLoading: updateLoading, reset: updateReset}] = useUpdatePersonMutation();
    const {data: person, isFetching: personLoading} = useGetPersonQuery(personID, {refetchOnMountOrArgChange: true});

    React.useEffect(() => {
        // Update form fields when person data changes.
        if (person) {
            form.setFieldsValue({
                'first_name': person.first_name,
                'last_name': person.last_name,
                'birth_date': person.birth_date ? dayjs(person.birth_date).tz() : undefined,
            });
        }
    }, [person, form]);

    React.useEffect(() => {
        if (updateSuccess) {
            showMessages('Personal Info successfully updated.', messageType.success);
            updateReset();
        } else if (isUpdateError) {
            showMessages((updateError as BaseError).data?.message, messageType.error);
        }
    }, [updateSuccess, updateReset, isUpdateError, updateError]);

    const validateBirthDate = (_: any, value: dayjs.Dayjs) => {
        return new Promise<void>((resolve, reject) => {
            if (value) {
                const today = dayjs();
                const age = today.diff(value, 'year');

                if (age >= 120) {
                    reject('Please enter a valid date.');
                } else {
                    resolve();
                }
            } else {
                reject('Please enter a birth date.');
            }
        });
    };

    const onSubmit = (data: Person) => {
        data = {
            ...data,
            birth_date: dayjs(data.birth_date).tz().format('YYYY-MM-DD'),
        };

        const personInfo = {
            first_name: person?.first_name,
            last_name: person?.last_name,
            birth_date: dayjs(person?.birth_date).tz().format('YYYY-MM-DD'),
        };

        if (JSON.stringify(personInfo) === JSON.stringify(data)) {
            showMessages(nothingToUpdate, messageType.error);
            return;
        }

        if (personID && data.birth_date !== 'Invalid Date') {
            updatePerson({...data, id: personID});
        }
    }

    return (
        <Spin spinning={personLoading || updateLoading} >
            <Card
                title="Personal Info"
                className="personal-info-card-main"
                bordered={true}
            >
                <Form
                    form={form}
                    name="personal-data"
                    layout="vertical"
                    {...formInCardLayout}
                    onFinish={onSubmit}
                    disabled={!isStaff}
                >
                    <Form.Item
                        name="first_name"
                        label="First Name"
                        rules={[{type: 'string', required: true, whitespace: true, message: "'First Name' is required"}]}
                    >
                        <Input name="first_name" disabled={!isStaff}/>
                    </Form.Item>
                    <Form.Item
                        name="last_name"
                        label="Last Name"
                        rules={[{type: 'string', required: true, whitespace: true, message: "'Last Name' is required"}]}
                    >
                        <Input name="last_name" disabled={!isStaff}/>
                    </Form.Item>
                    <Form.Item
                        name="birth_date"
                        label="Birth Date"
                        rules={[{
                            type: 'object',
                            required: true,
                            validator: validateBirthDate,
                            message: "'Birth Date' is required"
                        }]}
                    >
                        <DatePicker
                            placeholder="DD-MM-YYYY"
                            format={dateFormat}
                            style={{...formItemFullWidth}}
                            disabled={!isStaff}
                        />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" disabled={!isStaff}>
                        Save
                    </Button>
                </Form>
            </Card>
        </Spin>
    );
};

export default PersonalInfoCard;
